import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeDrivers } from 'Core/hooks';
import moment from 'moment';
// Components
import { Button, Form, PillsGroup, Select, SidePanel, SidePanelBody, SidePanelFooter, TimeFrame, useTimeFrameComponent } from '@gpstrackit/gtc-react-controls';
import { Grid } from '@mui/material';

// Actions
import { catalogLoad } from 'Redux/actions'
import { openModalAdvancedFilters, reset, searchDriverPerformanceCasesCoach } from './redux/slice';

// Helpers
import { getInitialRange } from '../../../../.globals/helpers';
// Styles
import './style.scss';

// Views
import TagsManage from "Modules/.common/components/TagsManage";

const AdvancedFilters = ({ tableRef, formData, setFormData, initialState, applyChanges, hasFilters, savePreferences, loading }) => {
  const dispatch = useDispatch()
  const { messages } = useIntl()
  const [tempFilters, setTempFilters] = useState({});
  const [entitySelected, setEntitySelected] = useState([]);
  const hasPendingChanges = useMemo(() => {
    const normalize = (filters) => {
      const safe = filters || {};
      return {
        timeFrame: safe.timeFrame || null,
        rangeDateStart: safe.rangeDate?.start ? String(safe.rangeDate.start) : null,
        rangeDateEnd: safe.rangeDate?.end ? String(safe.rangeDate.end) : null,
        coaches: (safe.coaches || []).map((c) => c?.id).filter(Boolean).sort(),
        status: (safe.status || []).map((s) => s?.id).filter(Boolean).sort(),
        tags: (safe.tags || []).map((t) => t?.id).filter((v) => v !== undefined && v !== null).sort(),
        drivers: (safe.drivers || []).map((d) => d?.driverId).filter(Boolean).sort(),
      };
    };

    return JSON.stringify(normalize(tempFilters)) !== JSON.stringify(normalize(formData));
  }, [tempFilters, formData]);
  // Redux States
  const { open, coaches, loadingCoach } = useSelector((state) => state.driverPerformanceCasesTabAdvancedFiltersRedux);
  const dateformat = useSelector(state => state.securityRedux.user.dateformat);
  const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
  const timeFrameOptions = [
    { id: "custom", label: messages['date_custom'] },
    { id: "today", label: messages['date_today'] },
    { id: "yesterday", label: messages['date_yesterday'] },
    { id: "week", label: messages['date_this_week'] },
    { id: "lastWeek", label: messages['date_last_week'] },
    { id: "thisMonth", label: messages['date_this_month'] },
    { id: "last7days", label: messages['date_last_7days'] },
    { id: "last15days", label: messages['date_last_15days'] },
    { id: "last30Days", label: messages['date_last_30days'] },
  ];
  const configButtons = [
    {
      id: 'Pending',
      label: messages['driver_performance_cases_filters_pending'],
      color: 'grey',
    },
    {
      id: 'Evaluate',
      label: messages['driver_performance_cases_filters_evaluate'],
      color: 'orange',
    },
    {
      id: 'Expired',
      label: messages['driver_performance_cases_filters_expired'],
      color: 'black',
    },
    {
      id: 'Completed',
      label: messages['driver_performance_cases_filters_completed'],
      color: '#66bb6a',
    }
  ];
  const timeFrame = useTimeFrameComponent(timeFrameOptions, "thisMonth")
  //Form refs
  const formRef = Form.useForm();

  // Callback to handle changes in TagsManage
  const onChangeEntityTagPicker = useCallback(
    (items, entity) => {
      let newItems = [];

      if (items.length) {
        newItems = items.filter((n) => n.id != -1);
      } else {
        newItems = [];
      }

      // Separate tags and drivers according to the "name" field
      const tags = newItems.filter(item => item.name === "Tag");
      const drivers = newItems.filter(item => item.name === "Driver");

      // Update tempFilters with separated tags and drivers
      setTempFilters({
        ...tempFilters,
        tags: tags.map(tag => ({ id: tag.id, label: tag.label })),
        drivers: drivers.map(driver => ({
          driverId: driver.id,
          name: driver.label
        }))
      });

      setEntitySelected(newItems);
    },
    [tempFilters]
  );

  // Definir entidades disponibles
  const entities = useMemo(
    () => [
      { id: "Tag", label: messages["driver_performance_events_filters_tags"] },
      { id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] },
    ],
    [messages]
  );

  useEffect(() => {
    if (open) {
      const { startDate, endDate } = timeFrame;
      let date = getInitialRange(timeFrame?.option?.id, startDate, endDate, true);

      // When custom timeframe, cap the range to 30 days
      if (timeFrame?.option?.id === 'custom' && date?.start && date?.end) {
        const start = moment(date.start);
        const end = moment(date.end);
        const diffDays = end.diff(start, 'days');

        if (diffDays > 29) {
          date.end = start.clone().add(29, 'days').endOf('day').toDate();
        }
      }

      let currentDateRange = {
        start: date?.start,
        end: date?.end
      }
      setTempFilters({
        ...tempFilters,
        rangeDate: currentDateRange,
        timeFrame: timeFrame?.option?.id
      })
    }
  }, [timeFrame?.startDate, timeFrame?.endDate]);
  useEffect(() => {
    return () => {
      dispatch(reset())
    }
  }, [])
  useEffect(() => {
    if (open && formData) {
      // Initialize tempFilters with current formData values when modal opens
      setTempFilters({ ...formData });

      // Synchronize entitySelected with formData values
      const selectedEntities = [];

      // Add tags to entitySelected
      if (formData?.tags && formData.tags.length > 0) {
        formData.tags.forEach(tag => {
          selectedEntities.push({
            id: tag.id,
            label: tag.label,
            name: "Tag"
          });
        });
      }

      // Add drivers to entitySelected
      if (formData?.drivers && formData.drivers.length > 0) {
        formData.drivers.forEach(driver => {
          selectedEntities.push({
            id: driver.driverId,
            label: driver.name,
            name: "Driver"
          });
        });
      }

      setEntitySelected(selectedEntities);

      if (formData?.timeFrame && formData?.rangeDate?.start && formData?.rangeDate?.end) {
        let currentTime = timeFrameOptions.find((item) => item?.id === formData.timeFrame)
        
        timeFrame.onChangeSelect(currentTime, formData?.rangeDate?.start, formData?.rangeDate?.end)
      }
      formRef?.setValues(formData);
    }
    if (open) {
      dispatch(searchDriverPerformanceCasesCoach({}))
    }
  }, [open])
  const clearFilters = () => {
    let currentDate = getInitialRange('thisMonth', null, null, true)
    const conditions = [{
      range: {
        fields: ["createdOn"],
        GTE: currentDate.start,
        LTE: currentDate.end
      }
    }, {
      page: 1
    }];
    //Refresh table with initial conditions
    tableRef.refresh({ _conditions: conditions, offset: 0 });
    const defaultPreferences = ["rangeDate", "timeFrame", "status", "coaches", "tags", "drivers"]
      .map(key => ({ key, value: null }));
    //Preferences
    savePreferences(defaultPreferences)
    const clearedData = {
      ...initialState,
      openSearch: formData?.openSearch || ""
    };
    setFormData(clearedData);
    setTempFilters(clearedData);

    // Clear entitySelected
    setEntitySelected([]);

    // Reset TimeFrame component
    formRef?.resetValues();
    timeFrame.reset();
  }
  useEffect(() => {
    if (open && drivers.length === 0) {
      dispatch(catalogLoad(['drivers']))
    }
  }, [open])
  return (
    <SidePanel
      isOpen={open}
      onToggle={() => dispatch(openModalAdvancedFilters({ open: !open }))}
      style={{ zIndex: 2, width: '380px', transform: 'translateX(380px)' }}
      classSidePanel={hasFilters() ? "dp-cases-tab-advanced-filters-available" : ""}
    >
      <SidePanelBody>
        <Grid container direction="row" justifyContent="space-between" alignItems="center">
          <Grid item>
            <h2 style={{ margin: 0 }}>
              {messages['workOrder_tab_advancedFilters_title']}
            </h2>
            <span
              className='btn-link'
              onClick={() => {
                if (!loading) {
                  clearFilters()
                }
              }}
              style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {messages['workOrder_tab_advancedFilters_clearAll']}
            </span>
          </Grid>
        </Grid>
        <div style={{ width: 330, marginTop: 10 }}>
          <Form
            ref={formRef.ref}
            loading={loadingCoach || loading}
            onValidSubmit={() => {
              // Apply temporary filters to formData with explicit page reset
              const filtersWithPageReset = { ...tempFilters, page: 1 };
              setFormData(filtersWithPageReset);
              // Save all preferences when applying filters
              savePreferences([{
                key: "rangeDate", value: tempFilters.rangeDate
              }, {
                key: "timeFrame", value: tempFilters.timeFrame
              }, {
                key: "coaches", value: tempFilters.coaches
              }, {
                key: "status", value: tempFilters.status
              }, {
                key: "drivers", value: tempFilters.drivers
              }, {
                key: "tags", value: tempFilters.tags
              }]);
              applyChanges('', '', filtersWithPageReset);
              // Close modal after applying filters to prevent multiple clicks
              dispatch(openModalAdvancedFilters({ open: false }));
            }}
            onChangeValues={() => { }}
            className="cases"
          >
            <Form.Item
              name="timeFrame"
              label={messages['driver_performance_cases_filters_timeFrame']}
              style={{ marginBottom: 10 }}
            >
              <div style={{ width: 330, marginTop: -10 }} className="dp-cases-tab-advanced-filters-timeframe">
                <TimeFrame
                  isRequired
                  filterSwitch={false}
                  {...timeFrame}
                  maxDate={new Date()}
                  dateFormat={dateformat || 'Y/m/d'}
                  withTime={false}
                  openDateRangePickerModalAutomatically={false}
                  startDate={moment(tempFilters?.rangeDate?.start || formData?.rangeDate?.start)}
                  endDate={moment(tempFilters?.rangeDate?.end || formData?.rangeDate?.end)}
                  customRangeDate={30}
                />
              </div>
            </Form.Item>
            <div style={{ marginBottom: 10 }}>
              <Select
                name="coaches"
                textField="fullname"
                label={messages['driver_performance_cases_filters_coach']}
                valueField="id"
                placeholder={messages['driver_performance_cases_filters_coach']}
                isMulti={true}
                options={coaches || []}
                isDisabled={false}
                isLoading={loadingCoach}
                isClearable={true}
                isSearchable={true}
                showPillsOutside={true}
                value={tempFilters?.coaches || []}
                onChange={(value) => {
                  setTempFilters({ ...tempFilters, coaches: value })
                }}
              />
            </div>
            <div>
              <div className="input-gtc-rc-label"><label className="label-gtc-rc">{messages['driver_performance_cases_filters_status']}</label></div>
              <PillsGroup
                values={tempFilters?.status}
                onClick={(value) => {
                  let status;
                  if (tempFilters?.status.find((item) => item.id === value.id)) {
                    status = tempFilters?.status.filter((item) => item.id !== value.id);
                  } else {
                    status = [...(tempFilters?.status || []), value];
                  }
                  setTempFilters({ ...tempFilters, status })
                }}
                options={configButtons}
                style={{
                  margin: '3px',
                }}
                multiple
                multicolor
              />
            </div>
            <div>
              <div className="cases-tab-tags" style={{ width: '100%', overflow: "auto", marginTop: 5 }}>
                <div className="input-gtc-rc-label"><label className="label-gtc-rc">{messages['driver_performance_cases_filters_tags']}</label></div>
                <TagsManage
                  entities={entities}
                  onChange={onChangeEntityTagPicker}
                  hideTagsWithoutEntities={true}
                  mode="list"
                  height={400}
                  value={entitySelected}
                  noPills={true}
                  entitySelected={{ id: "Driver", label: messages["clipRequestAdvancedFilters_drivers"] }}
                  searchByTag={true}
                />
              </div>
            </div>
          </Form>
        </div>
      </SidePanelBody>
      <SidePanelFooter>
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            isDisabled={loadingCoach || loading}
            style={{ marginRight: 8 }}
            onClick={() => dispatch(reset({ open: false }))}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>
          <Button
            isDisabled={loadingCoach || loading || (!timeFrame?.startDate || !timeFrame?.endDate) || !hasPendingChanges}
            onClick={() => formRef?.submit()}
            variant="primary"
          >
            {messages["apply"]}
          </Button>
        </div>
      </SidePanelFooter>
    </SidePanel>
  )
}
export default AdvancedFilters
